import { message } from 'antd';
import { lazy, useEffect } from 'react';
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';
import './App.scss';
import { Mode, ModuleHistory } from './models/common';
import { appRoutes } from './router/routes';
import PersistSessionParams from './components/PersistSessionParams';
import { useStore } from './store'; // <-- thêm
import Start from '../src/components/start';

const V2QA = lazy(() => import('./pages/Commons/V2/QA'));
const V2OneStopUserManual = lazy(() => import('./pages/Commons/V2/OneStopUserManual'));
const V2SearchCoupon = lazy(() => import('./pages/Furusato/V2/Search'));
const V2Search1Pt = lazy(() => import('./pages/Furusato/V2/Search1Pt'));
const V2SuccessfulUseFuru = lazy(() => import('./pages/Furusato/V2/SuccessfulUseFuru'));
const V2SuccessNotification = lazy(() => import('./pages/Furusato/V2/SuccessNotification'));
const V2SuccessNotificationMetaps = lazy(() => import('./pages/Furusato/V2/SuccessNotification/metaps'));
const V2ThankYouGift = lazy(() => import('./pages/Furusato/V2/ThankYouGift'));
const V2Notifications = lazy(() => import('./pages/Furusato/V2/Notifications'));
const V2NotificationDetail = lazy(() => import('./pages/Furusato/V2/NotificationDetail'));
const V2Simulation = lazy(() => import('./pages/Furusato/V2/Simulation'));
const V2HowToUse = lazy(() => import('./pages/Furusato/V2/HowToUse'));
const V2Top = lazy(() => import('./pages/Furusato/V2/Top'));
const PrivateRoute = lazy(() => import('./router/privateRoute'));
const V2WhatIsFurusatoNozei = lazy(() => import('./pages/Furusato/V2/WhatIsFurusatoNozei'));
const MainLayoutFurusato = lazy(() => import('./layout/Main'));
const V2MainLayoutFurusato = lazy(() => import('./layout/Furusato/V2/Main'));
const V2GiftVoucherDetail = lazy(() => import('./pages/Furusato/V2/GiftVoucherDetail'));
const V2InfoMember = lazy(() => import('./pages/Commons/V2/InfoMember'));
const NotFound = lazy(() => import('./pages/Commons/NotFound'));
const V2History = lazy(() => import('./pages/Furusato/V2/History'));
const V2HistoryPointExpired = lazy(() => import('./pages/Furusato/V2/HistoryPointExpired'));
const V2HistoryDetail = lazy(() => import('./pages/Furusato/V2/HistoryDetail'));
const V2FurusatoPayment = lazy(() => import('./pages/Furusato/V2/FurusatoPayment'));
const V2Release = lazy(() => import('./pages/Furusato/V2/Release'));
const Login = lazy(() => import('./pages/Commons/Login'));
const Register = lazy(() => import('./pages/Commons/Register'));
const V2Store = lazy(() => import('./pages/Furusato/V2/Stores'));
const V2Terms = lazy(() => import('./pages/Furusato/V2/Terms'));

/** Inline guard bên trong Router context */
function PaymentRouteGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { custom_path } = useParams();
  useEffect(() => {
    const cid = sessionStorage.getItem('cid');
    const groupId = sessionStorage.getItem('groupId');

    const isMissing = (v: string | null) =>
      !v || v === 'null' || v === 'undefined' || v.trim() === '';
    if (isMissing(cid) && isMissing(groupId)) {
      const to = `/${custom_path}/${appRoutes.FURUSATO_NOZEI.LOGIN}`;
      navigate(to, { replace: true });
    }
  }, [location.pathname, navigate,custom_path]);

  return <>{children}</>;
}

function App() {
  message.config({ top: 40 });

  return (
    <BrowserRouter basename={process.env.REACT_APP_BASE_PATH}>
      <PersistSessionParams />
      <Routes>
        <Route path="/" element={<Start />} />
        <Route path={`${appRoutes.FURUSATO_NOZEI.ROOT}`} element={<PrivateRoute />}>
          <Route element={<MainLayoutFurusato />}></Route>
        </Route>

        <Route element={<PrivateRoute />}>
          <Route path=":custom_path" element={ <PaymentRouteGuard><V2MainLayoutFurusato /></PaymentRouteGuard>}>
            <Route path={`${appRoutes.FURUSATO_NOZEI.TOP}`} element={<V2Top />} />
            <Route path={`${appRoutes.FURUSATO_NOZEI.THANK_YOU_GIFT}`} element={<V2ThankYouGift />} />
            <Route path={`${appRoutes.FURUSATO_NOZEI.THANK_YOU_GIFT}/:fc_id`} element={<V2ThankYouGift />} />
            <Route
              path={`${appRoutes.FURUSATO_NOZEI.THANK_YOU_GIFT_DETAIL}/:id`}
              element={<V2GiftVoucherDetail mode={Mode.gift} />}
            />
            <Route
              path={`:fc_id/${appRoutes.FURUSATO_NOZEI.THANK_YOU_GIFT_DETAIL}/:id`}
              element={<V2GiftVoucherDetail mode={Mode.gift} />}
            />

            {/* Áp dụng guard CHỈ cho route payment */}
            <Route path={`${appRoutes.FURUSATO_NOZEI.PAYMENT}`} element={<V2FurusatoPayment />} />

            <Route path={`${appRoutes.FURUSATO_NOZEI.SUCCESS_NOTIFICATION}`} element={<V2SuccessNotification />} />
            <Route path={`${appRoutes.FURUSATO_NOZEI.SUCCESS_NOTIFICATION_METAPS}`} element={<V2SuccessNotificationMetaps />} />
            <Route path={`${appRoutes.FURUSATO_NOZEI.RELEASE}`} element={<V2Release />} />
            <Route path={`${appRoutes.FURUSATO_NOZEI.SEARCH_COUPON}`} element={<V2SearchCoupon mode={Mode.gift} />} />
            <Route path={`${appRoutes.FURUSATO_NOZEI.SEARCH_1PT}`} element={<V2Search1Pt mode={Mode.gift} />} />
            <Route path={`${appRoutes.FURUSATO_NOZEI.SUCCESSFUL_USE}`} element={<V2SuccessfulUseFuru />} />
            <Route
              path={`${appRoutes.FURUSATO_NOZEI.HISTORY_POINT_EXPIRED}`}
              element={<V2HistoryPointExpired module={ModuleHistory.point_expired} />}
            />
            <Route
              path={`${appRoutes.FURUSATO_NOZEI.HISTORY}`}
              element={<V2History module={ModuleHistory.product} />}
            />
            <Route
              path={`${appRoutes.FURUSATO_NOZEI.HISTORY}/detail`}
              element={<V2HistoryDetail module={ModuleHistory.product} />}
            />
            <Route
              path={`${appRoutes.FURUSATO_NOZEI.USE_HISTORY}`}
              element={<V2History module={ModuleHistory.coupon} />}
            />
            <Route
              path={`${appRoutes.FURUSATO_NOZEI.USE_HISTORY}/detail`}
              element={<V2HistoryDetail module={ModuleHistory.coupon} />}
            />
            <Route path={`${appRoutes.FURUSATO_NOZEI.STORES}`} element={<V2Store />} />
            <Route path={appRoutes.FURUSATO_NOZEI.WHAT_IS_FURUSATO_NOZEI} element={<V2WhatIsFurusatoNozei />} />
            <Route path={`${appRoutes.FURUSATO_NOZEI.QA}`} element={<V2QA mode={Mode.gift} />} />
            <Route
              path={`${appRoutes.FURUSATO_NOZEI.ONE_STOP_USER_MANUAL}`}
              element={<V2OneStopUserManual mode={Mode.gift} />}
            />
            <Route path={`${appRoutes.FURUSATO_NOZEI.NOTIFICATIONS}`} element={<V2Notifications />} />
            <Route path={`${appRoutes.FURUSATO_NOZEI.NOTIFICATIONS}/:notification_id`} element={<V2NotificationDetail />} />
            <Route path={`${appRoutes.FURUSATO_NOZEI.SIMULATION}`} element={<V2Simulation mode={Mode.gift} />} />
            <Route path={`${appRoutes.FURUSATO_NOZEI.HOW_TO_USE}`} element={<V2HowToUse />} />
            <Route path={`${appRoutes.FURUSATO_NOZEI.TERMS}`} element={<V2Terms mode={Mode.gift} />} />
            <Route path={`${appRoutes.FURUSATO_NOZEI.INFO_MEMBER}`} element={<V2InfoMember mode={Mode.gift} />} />
            <Route path={`${appRoutes.FURUSATO_NOZEI.LOGIN}`} element={<Login />} />
            <Route path={`${appRoutes.FURUSATO_NOZEI.REGISTER}`} element={<Register />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
